import { Box, IconProps, Portal } from "@chakra-ui/react";
import { ReactElement, cloneElement, useCallback, useEffect, useRef } from "react";

import { useOnlyOnce } from "@nens/react/hooks";
type FancyFaviconProps = {
  children?: ReactElement<IconProps> | null;
  isRotating?: boolean;
};

const getLink = () => {
  document.querySelectorAll("link[rel*='icon']").forEach((el) => el.remove());

  const link =
    (document.getElementById("favicon") as HTMLLinkElement | null) ||
    (() => {
      const link = document.createElement("link");
      link.id = "favicon";
      link.rel = "icon";
      document.head.appendChild(link);
      return link;
    })();
  return link;
};

const getSvgUrl = (wrapper: HTMLDivElement | null) => {
  if (!wrapper) return;
  const svgEl = wrapper.querySelector("svg") as Element | undefined;
  if (!svgEl) return;

  const computedColor = getComputedStyle(svgEl).color;
  const replaceCurrentColor = (el: Element) => {
    const stroke = el.getAttribute("stroke");
    const fill = el.getAttribute("fill");

    if (stroke === "currentColor") el.setAttribute("stroke", computedColor);
    if (fill === "currentColor") el.setAttribute("fill", computedColor);

    Array.from(el.children).forEach(replaceCurrentColor);
  };
  replaceCurrentColor(svgEl);

  const svgString = new XMLSerializer().serializeToString(svgEl);
  const encoded = encodeURIComponent(svgString);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;
  return dataUrl;
};

export function FancyFavicon({ children, isRotating }: FancyFaviconProps) {
  const angleRef = useRef(0);
  const timerRef = useRef<number>();
  const lastTimeRef = useRef<number>(Date.now());
  const originalHrefRef = useRef<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const wrapperInnerHTML = wrapperRef.current?.innerHTML;

  useOnlyOnce(() => {
    const originalLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (originalLink?.href) {
      originalHrefRef.current = originalLink.href;
    }
  });

  const stopRotating = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    angleRef.current = 0;
  }, []);

  useEffect(() => {
    const url = getSvgUrl(wrapperRef.current) ?? originalHrefRef.current;
    if (!url) return;

    const link = getLink();

    if (!isRotating) {
      link.href = url;
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.src = url;
    img.crossOrigin = "anonymous"; // helps with CORS issues for canvas

    const updateFavicon = () => {
      if (!ctx) return;

      ctx.clearRect(0, 0, 32, 32);
      ctx.save();
      ctx.translate(16, 16);
      ctx.rotate(angleRef.current);
      ctx.translate(-16, -16);

      ctx.drawImage(img, 0, 0, 32, 32);
      ctx.restore();

      link.href = canvas.toDataURL("image/png");

      const now = Date.now();
      const delta = now - lastTimeRef.current;
      lastTimeRef.current = now;
      const radiansPerMs = 0.001; // speed per millisecond
      angleRef.current += delta * radiansPerMs;
    };

    const startRotating = () => (timerRef.current = window.setInterval(updateFavicon, 1));

    img.onload = startRotating;

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [isRotating, stopRotating, wrapperInnerHTML]);

  // Cleanup
  useEffect(stopRotating, [stopRotating]);
  useEffect(() => {
    if (!isRotating) stopRotating();
  }, [isRotating, stopRotating]);
  useEffect(() => {
    return () => {
      const originalHref = originalHrefRef.current;
      if (originalHref) getLink().href = originalHref;
    };
  }, []);

  return (
    <Portal>
      <Box display="none" ref={wrapperRef}>
        {children && cloneElement(children, { boxSize: 8 })}
      </Box>
    </Portal>
  );
}
